import { ApiProductCategory, ApiPromotion } from '@vend/types'
import { fetchApi, trackError } from '@vend/utilities'

// Rough shape of the payload returned from /api/2.0/discount
export interface PromotionsApiResponse {
  data?: PromotionsData
}

export interface PromotionsData {
  sale: {
    line_items: PromotionLineItem[]
  }
  promo_code_validation?: {
    status: string
    promotion_id: string
    promo_code: string | null
    promo_code_id: string | null
  }
  promotions: ApiPromotion[]
  potential_promotion?: {
    id: string
  }
}

export interface PromotionLineItem {
  id: string
  promotion_ids: string[]
  unit_price: number | string
  unit_discount: number | string
  unit_tax: number | string
  unit_tax_discount: number | string
  loyalty_value: number | string
  explicit_loyalty_value: boolean
}

export interface PromotionsPayload {
  channel: 'Register'
  promo_code: string
  customer: {
    customer_group_id: string | null
  }
  is_loyalty_enabled: boolean
  is_tax_inclusive: boolean
  outlet_id: string
  sale: {
    sale_date: string
    line_items: PromotionsPayloadLineItem[]
  }
}

export interface PromotionsPayloadLineItem {
  id: string
  handle: string
  product_id: string
  product_brand_id: string | null
  product_supplier_id: string | null
  product_type_id: string | null
  product_category: ApiProductCategory | null
  product_tag_ids: string[]
  quantity: number
  unit_price: string | number
  unit_tax: string | number
  price_adjusted?: boolean
  confirmed: boolean
  loyalty_value: string | number
  explicit_loyalty_value: boolean
  variant_parent_id: string | null
}

/**
 * Check the available promotions for a sale represented by the given payload.
 *
 * @method fetchPromotionsData
 *
 * @param {PromotionsPayload} promotionsPayload
 *        The payload representing a sale to query the discount API for potential/applicable promotions
 *
 * @return {Promise<PromotionsApiResponse>} A promise resolved with the available promotions for a given sale
 */
export async function fetchPromotionsData(
  promotionsPayload: PromotionsPayload
): Promise<PromotionsApiResponse | null> {
  const response = await fetchApi('/api/2.0/discount', {
    body: JSON.stringify(promotionsPayload),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
  })

  if (!response.ok) {
    trackError(`Failed to fetch promotions data: ${response.statusText}`)
    return null
  }

  return (await response.json()) as PromotionsApiResponse
}
